import React, {cloneElement, forwardRef, isValidElement, ReactElement, useEffect, useImperativeHandle, useRef} from "react";
import {FormProvider, useForm, UseFormReturn, type FieldErrors,} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {classNames} from "primereact/utils";
import AppDialog, {AppDialogProps} from "./AppDialog";

interface CrudDialogProps extends Omit<AppDialogProps, 'visible' | 'onHide' | 'footer' | 'footerActions'> {
    title: string;
    onSubmit: ((data: any, methods: UseFormReturn) => void) | ((data: any) => void);
    visible: boolean;
    view?: boolean;
    onHide: () => void;
    initialValues?: any;
    actions?: DialogAction[];
    errors?: string[];
    onValidationError?: (errors: FieldErrors, methods: UseFormReturn) => void;
    children: React.ReactNode;
    setMethod? : (value: any) => void;
    saveDisabled?: boolean;
    saveLabel?: string;
    /** Optional footer action rendered left of save (e.g. marketplace run-now). */
    secondaryFooterAction?: CrudDialogSecondaryFooterAction;
    /** Multiple outlined footer actions left of save (order preserved). */
    secondaryFooterActions?: CrudDialogSecondaryFooterAction[];
    /** Called when the primary save button is clicked, before form submit. */
    onBeforePrimarySubmit?: () => void;
}

export interface CrudDialogSecondaryFooterAction {
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    icon?: string;
    /** When true, runs onClick then submits the form (e.g. save-and-connect). */
    submitsForm?: boolean;
}

export interface DialogAction {
    title: string;
    type?: "submit" | "reset" | "button" | undefined;
    icon?: string;
    action?: any;
}

export interface CrudDialogRef {
    setField: (field: string, value: any) => void;
    resetField: (field: string) => void;
    resetDialog: (data?: any) => void;
    clearValueFromField: (field: string) => void;
}

const CrudDialog = forwardRef<CrudDialogRef, CrudDialogProps>(({
                                                                   title,
                                                                   children,
                                                                   onSubmit,
                                                                   visible,
                                                                   view = false,
                                                                   onHide,
                                                                   initialValues = {},
                                                                   actions,
                                                                   setMethod,
                                                                   saveDisabled,
                                                                   saveLabel,
                                                                   secondaryFooterAction,
                                                                   secondaryFooterActions,
                                                                   onBeforePrimarySubmit,
                                                                   onValidationError,
                                                                   className,
                                                                   ...dialogProps
                                                               }: CrudDialogProps, ref: React.Ref<CrudDialogRef | undefined>) => {
    const {t} = useTranslation();
    const methods = useForm({ mode: "onSubmit", reValidateMode: "onChange", shouldFocusError: true });
    const previousInitialValuesRef = useRef<any>();

    useEffect(() => {
        if (visible) {
            const initialValuesString = JSON.stringify(initialValues);
            const previousInitialValuesString = JSON.stringify(previousInitialValuesRef.current);

            if (initialValuesString !== previousInitialValuesString) {
                methods.reset(initialValues);
                methods.reset(initialValues);
                previousInitialValuesRef.current = initialValues;
            }
        }
    }, [visible, initialValues, methods]);

    useEffect(() => {
        if (setMethod) {
            setMethod(methods)
        }
    }, [setMethod, methods]);

    useImperativeHandle(ref, () => ({
        resetField: (field: string) => methods.resetField(field),
        resetDialog: (data: any) => methods.reset(data),
        clearValueFromField: (field: string) => methods.setValue(field, null),
        setField: (field: string, value: any) => methods.setValue(field, value),
    }));

    const submitForm = () => {
        void methods.handleSubmit(
            (data) => onSubmit(data, methods),
            (errors) => onValidationError?.(errors, methods),
        )();
    };

    const resolveFooter = (): Pick<AppDialogProps, 'footer' | 'footerActions'> => {
        if (actions != null) {
            if (actions.length === 0) {
                return {};
            }

            return {
                footer: (
                    <div className="app-dialog-footer">
                        {actions.map((action, index) => {
                            const isPrimary = action.type === 'submit' || index === actions.length - 1;
                            const handleClick = () => {
                                if (action.type === 'submit') {
                                    action.action?.();
                                    submitForm();
                                    return;
                                }
                                action.action?.();
                            };

                            return (
                                <button
                                    key={index}
                                    type="button"
                                    className={isPrimary ? 'app-dialog-footer__submit' : 'app-dialog-footer__cancel'}
                                    onClick={handleClick}
                                >
                                    {action.icon ? <i className={action.icon} aria-hidden /> : null}
                                    {action.title}
                                </button>
                            );
                        })}
                    </div>
                ),
            };
        }

        if (view) {
            return {
                footer: (
                    <div className="app-dialog-footer">
                        <button
                            type="button"
                            className="app-dialog-footer__cancel"
                            onClick={onHide}
                        >
                            {t("common.cancel")}
                        </button>
                    </div>
                ),
            };
        }

        const resolvedSecondaryActions = [
            ...(secondaryFooterActions ?? []),
            ...(secondaryFooterAction ? [secondaryFooterAction] : []),
        ].map((action) => ({
            label: action.label,
            icon: action.icon,
            disabled: action.disabled,
            onClick: () => {
                action.onClick?.();
                if (action.submitsForm) {
                    submitForm();
                }
            },
        }));

        return {
            footerActions: {
                cancelLabel: t("common.cancel"),
                onCancel: onHide,
                secondaryActions: resolvedSecondaryActions,
                submitLabel: saveLabel ?? t("common.save"),
                submitType: "button",
                onSubmit: () => {
                    onBeforePrimarySubmit?.();
                    submitForm();
                },
                submitDisabled: saveDisabled,
            },
        };
    };

    const footerProps = resolveFooter();

    return (
        <AppDialog
            header={title}
            visible={visible}
            onHide={onHide}
            className={classNames('dialog', className)}
            {...dialogProps}
            {...footerProps}
        >
            <FormProvider {...methods}>
                <form
                    className="grid grid-nogutter"
                    onSubmit={methods.handleSubmit(
                        (data) => onSubmit(data, methods),
                        (errors) => onValidationError?.(errors, methods),
                    )}
                >
                    {React.Children.map(children, child => {
                        if (!isValidElement(child)) return null;
                        return cloneElement(child as ReactElement, {
                            key: child.key,
                            className: classNames('col-12 mb-2 grid grid-nogutter', child.props.className),
                        });
                    })}
                </form>
            </FormProvider>
        </AppDialog>
    );
});

export {
    CrudDialog
}
